import React, { useEffect, useRef, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  TableContainer,
  TableContent,
  TableHeader,
  TABLE_LAYOUT_OPTIONS,
  usePreventTableScrollingRef,
  Loader,
  TextButton,
  Radio,
  RADIO_LABEL_POSITIONS,
  RADIO_SIZES,
  Switch,
  Dialog,
  BUTTON_VARIANTS,
} from "cdk-radial";
import { BUNDLE_DOC_TYPES } from "../Utils/constants.js";
import classNames from "classnames";
import config from "../config/app.conf.json";
import moment from "moment";

const EllipsisCellRenderer = ({ value }) => (
  <div className="ellipsis_cell_renderer">{value || ""}</div>
);

export function formatDate(dateString) {
  const date = new Date(dateString);
  const options = { month: "short", day: "numeric", year: "numeric" };
  let formattedDate = date.toLocaleDateString("en-US", options);

  const day = date.getDate();
  const suffixes = ["th", "st", "nd", "rd"];
  const suffix =
    suffixes[
      day % 10 < 4 && day % 10 > 0 && (day < 11 || day > 13) ? day % 10 : 0
    ];

  formattedDate = formattedDate
    .replace(/,/, "")
    .replace(/(\d+)/, "$1" + suffix);
  return formattedDate;
}

export default function BundleDocsList({
  bundleDocType,
  bundleDocs,
  setExternalDocumentList,
  bundleId,
  accessToken,
  selectedDocId,
  setSelectedDocId,
  setActivateDocIds,
  setDeactivateDocIds,
  initialExternalDocIds,
  setInitialExternalDocIds,
  setUploadError,
  setShowErrorDialog,

  deleteDetailsDoc,

  activeDocId,
  setActiveDocId,
}) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCannotDeleteDialog, setShowCannotDeleteDialog] = useState(false);
  const [retireDocId, setRetireDocId] = useState(null);
  const [retireDocName, setRetireDocName] = useState(null);
  const gridRef = useRef();
  const tableContentRef = usePreventTableScrollingRef();

  const initializeData = useCallback(() => {
    let dataToProcess = bundleDocs;
    if (dataToProcess && dataToProcess.length > 0) {
      const tableData = dataToProcess.map((doc) => {
        let formattedDate = "NA";
        if (doc?.created) {
          const date = moment(doc.created);
          formattedDate = date.format("MM/DD/YYYY - hh:mm A");
        }
        if (
          doc?.status === "active" &&
          activeDocId === null &&
          selectedDocId === null &&
          bundleDocType === BUNDLE_DOC_TYPES.TERMS
        ) {
          setActiveDocId(doc?.id);
          setSelectedDocId(doc?.id);
        }

        if (bundleDocType === BUNDLE_DOC_TYPES.EXTERNAL) {
          if (doc?.status === "active") {
            setInitialExternalDocIds((prev) => ({
              ...prev,
              active: [...(prev?.active || []), doc?.id],
            }));
          } else if (doc?.status === "inactive") {
            setInitialExternalDocIds((prev) => ({
              ...prev,
              inactive: [...(prev?.inactive || []), doc?.id],
            }));
          }
        }
        return {
          fileName: doc?.fileName,
          docName: doc?.docName,
          updated_on: formattedDate,
          docUrl: doc?.docUrl,
          status: doc?.status,
          docId: doc?.id,
        };
      });

      const sortedTableData = tableData.sort((a, b) => {
        if (!a.rawUpdatedDate && !b.rawUpdatedDate) return 0;
        if (!a.rawUpdatedDate) return 1;
        if (!b.rawUpdatedDate) return -1;

        return new Date(b.rawUpdatedDate) - new Date(a.rawUpdatedDate);
      });

      const finalTableData = sortedTableData.map(
        ({ rawUpdatedDate, ...rest }) => rest,
      );

      setData(finalTableData);
      setIsLoading(false);
    } else {
      setData([]);
      setSelectedDocId(null);
      setIsLoading(false);
    }
  }, [bundleDocs]);

  useEffect(() => {
    initializeData();
  }, [bundleDocs]);

  const CustomNoRowsOverlay = () => (
    <div className="empty_no_rows_overlay">
      <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
        No documents available
      </div>
    </div>
  );
  const viewTermsDocumentRenderer = ({ data, api, node }) => {
    const { docId } = data;
    const docRowIndex = `terms-doc-${docId}`;
    const handleDocSelect = () => {
      setSelectedDocId(docId);
    };
    return (
      <div>
        <Radio
          className="fdn-docs__radio"
          checked={node.isSelected()}
          value={docId}
          name="documentSelection"
          id={`radio-${docRowIndex}`}
          onChange={() => {
            api.deselectAll();
            node.setSelected(true);
            handleDocSelect();
          }}
          labelPosition={RADIO_LABEL_POSITIONS.AFTER}
          size={RADIO_SIZES.LARGE}
        />
      </div>
    );
  };

  const viewDetailsDocumentRenderer = ({ data, api, node }) => {
    const { docId } = data;
    const docRowIndex = `details-doc-${docId}`;
    return (
      <div>
        <Switch
          checked={node.isSelected()}
          name="detailsDocumentSelection"
          id={`switch-${docRowIndex}`}
          onChange={() => {
            if (node.isSelected()) {
              node.setSelected(false);
              setActivateDocIds((prev) => prev.filter((id) => id !== docId));
              if (!initialExternalDocIds?.inactive.includes(docId)) {
                setDeactivateDocIds((prev) => [...prev, docId]);
              }
            } else {
              node.setSelected(true);
              setDeactivateDocIds((prev) => prev.filter((id) => id !== docId));
              if (!initialExternalDocIds?.active.includes(docId)) {
                setActivateDocIds((prev) => [...prev, docId]);
              }
            }
          }}
        />
      </div>
    );
  };

  const handleFileDownload = async (data) => {
    try {
      const response = await fetch(
        `${config.api.api_gateway_url}/v1/admin/bundle/${bundleId}/documents`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Failed to download file");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const fileNameRenderer = ({ data }) => {
    const handleOpenPdf = () => {
      try {
        if (data.docUrl) {
          window.open(data.docUrl, "_blank");
        } else {
          handleFileDownload(data);
        }
      } catch (error) {
        console.error("Error opening PDF file:", error);
      }
    };
    return (
      <TextButton
        dataTestId="text-button"
        iconColor="primary"
        iconSize={24}
        onClick={handleOpenPdf}
        size="standard"
        text={data.fileName}
      />
    );
  };

  const handleRetireDoc = async () => {
    try {
      setIsLoading(true);
      await deleteDetailsDoc(retireDocId);
      const updatedList = bundleDocs.filter((doc) => doc.id !== retireDocId);
      setActivateDocIds((prev) => prev.filter((id) => id !== retireDocId));
      setDeactivateDocIds((prev) => prev.filter((id) => id !== retireDocId));
      setRetireDocId(null);
      setRetireDocName(null);
      setExternalDocumentList(updatedList);
      setIsLoading(false);
    } catch (error) {
      console.error("Error retiring document:", error);
      setRetireDocId(null);
      setRetireDocName(null);
      setIsLoading(false);
      setUploadError('Failed to delete bundle details document');
      setShowErrorDialog(true);
    }
  };

  const retiredDocumentRenderer = ({ data }) => {
    const handleDeleteClick = () => {
      setRetireDocId(data.docId);
      setRetireDocName(data.docName);
      setShowCannotDeleteDialog(true);
    };

    return <TextButton text="Delete" onClick={handleDeleteClick} />;
  };

  const columnsData = [
    {
      field: "action",
      headerName: "Action",
      cellClass: "action-cell",
      cellRendererFramework:
        bundleDocType === BUNDLE_DOC_TYPES.TERMS
          ? viewTermsDocumentRenderer
          : viewDetailsDocumentRenderer,
      width: 150,
      sortable: true,
      resizable: true,
    },
    {
      cellRendererFramework: EllipsisCellRenderer,
      field: "docName",
      headerName: "Document Name",
      width: 600,
      sortable: true,
      resizable: true,
    },
    {
      cellRendererFramework: fileNameRenderer,
      field: "fileName",
      headerName: "Document Link",
      width: 340,
      sortable: true,
      resizable: true,
    },

    {
      cellRendererFramework: EllipsisCellRenderer,
      field: "updated_on",
      headerName: "Uploaded",
      width: bundleDocType === BUNDLE_DOC_TYPES.TERMS ? 322 : 183,
      sortable: true,
      resizable: true,
    },
    {
      field: "retire_document",
      headerName: "Delete",
      cellClass: "retire-document-cell",
      cellRendererFramework: retiredDocumentRenderer,
      width: 133,
      hide: bundleDocType === BUNDLE_DOC_TYPES.TERMS,
      sortable: true,
      resizable: true,
    },
  ];

  const handleGridReady = useCallback((gridParams) => {
    let selected = false;
    if (bundleDocType === BUNDLE_DOC_TYPES.EXTERNAL) {
      gridParams.api.forEachNode((node) => {
        const data = node.data;
        if (data && data.status === "active") {
          node.setSelected(true);
        }
      });
    } else {
      gridParams.api.forEachNode((node) => {
        const data = node.data;
        if (!selected && data && data.status === "active") {
          node.setSelected(true);
          selected = true;
        }
      });
    }
    gridParams.api.sizeColumnsToFit();
    gridParams.api.refreshCells({ force: true });
  }, []);

  const onRowSelected = useCallback((e) => {
    if (!e.node.isSelected() && bundleDocType === BUNDLE_DOC_TYPES.TERMS)
      return;
    if (bundleDocType === BUNDLE_DOC_TYPES.TERMS) {
      e.api.forEachNode((n) => {
        if (n !== e.node && n.isSelected()) n.setSelected(false);
      });
    }
    e.api.refreshCells({ force: true, columns: ["action"] });
  }, []);

  const headerProps = {
    dataTestId: `bundles-table-header`,
    headerTitle:
      bundleDocType === BUNDLE_DOC_TYPES.TERMS
        ? "Terms & Conditions"
        : "Bundle Details",
    hideBorder: false,
    hideTableHeader: true,
    hideTableTitle: false,
    id: `contracted-bundles-table-header`,
    isAlwaysExpanded: false,
    isSearchable: false,
    isPrintable: false,
    isDownloadable: false,
    overflowButtonProps: undefined,
    customActions: (
      <TextButton
        text="Reload Data"
        onClick={{}}
        disabled={isLoading}
        data-testid="reload-pricing-data-btn"
        style={{ marginLeft: "10px" }}
      />
    ),
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <Loader
          className="bundle-docs-loader"
          data-testid="bundle_docs_loader"
          label="Loading documents..."
          variant="inline"
        />
      </div>
    );
  }

  return (
    <>
      {showCannotDeleteDialog && (
        <Dialog
          className="cannot-delete-doc-dialog"
          buttonsProps={[
            {
              id: "cancel-action",
              onClick: () => setShowCannotDeleteDialog(false),
              text: "Cancel",
              variant: BUTTON_VARIANTS.SECONDARY,
            },
            {
              id: "ok-action",
              onClick: () => {
                setShowCannotDeleteDialog(false);
                handleRetireDoc();
              },
              text: "Delete Document",
              variant: BUTTON_VARIANTS.PRIMARY,
            },
          ]}
          id="delete-doc-confirm-dialog"
          isOpen={showCannotDeleteDialog}
          onClose={() => setShowCannotDeleteDialog(false)}
          title="Delete Document?"
        >
          <div>
            This will permanently delete <strong>{retireDocName}</strong>.
          </div>
        </Dialog>
      )}
      <TableContainer
        id={
          bundleDocType === BUNDLE_DOC_TYPES.TERMS
            ? "terms-docs-grid"
            : "external-docs-grid"
        }
        className={classNames("bundles-documents-tab_grid", "ag-theme-balham")}
      >
        <TableHeader {...headerProps} />

        <TableContent
          data-testid="table-content"
          className="bundle-documents_tab_grid-content"
          ref={tableContentRef}
        >
          <AgGridReact
            columnDefs={columnsData}
            domLayout={TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT}
            rowData={data}
            rowHeight={50}
            style={{ width: "100%" }}
            ref={gridRef}
            onGridReady={handleGridReady}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            suppressScrollOnNewData={true}
            onRowSelected={onRowSelected}
            frameworkComponents={{
              customNoRowsOverlay: CustomNoRowsOverlay,
            }}
            noRowsOverlayComponent="customNoRowsOverlay"
          />
        </TableContent>
      </TableContainer>
    </>
  );
}
